import React, { useState, useEffect, useRef } from 'react';
import { BiExport, BiMessageDetail, BiHash, BiGroup, BiFilterAlt, BiErrorCircle, BiSortAlt2, BiDownArrowAlt, BiUpArrowAlt, BiTimeFive, BiUser } from 'react-icons/bi';
import { BsThreeDots, BsDownload } from 'react-icons/bs';

import { CSVLink } from 'react-csv';
import { orderBy } from 'lodash';
import { Popover, PopoverBody, Modal, ModalHeader, ModalBody } from 'reactstrap';

import axios from '../../../axios';
import * as url from '../../../utils/url';
import { formatTimeAgo } from '../../../utils/stringUtils';
import { toastContainer } from '../../../utils/toast';
import { loginImgTrivvy, USER_CHATS_EMPTY_STATE_MSG } from '../../../utils/constants';
import BottomScrollListener from 'react-bottom-scroll-listener';
import OoPopover from '../../../components/UI/Popover/OoPopover';

const HashtagOverview = ({ history, match, user }) => {
    var orgPrefix = (user && user.profile && user.profile.organizationId && user.profile.organizationId.smsGroupPrefix) || '';
    // Strip trailing dash if present
    if (orgPrefix.endsWith('-')) orgPrefix = orgPrefix.slice(0, -1);
    const containerRef = useRef(null);
    const headerWrapRef = useRef(null);
    // When the body scrolls horizontally (mobile, where the table has a
    // min-width that exceeds the viewport), keep the header table in sync
    // so its columns stay aligned with the body's columns.
    const syncHeaderHorizontalScroll = (e) => {
        if (headerWrapRef.current) {
            headerWrapRef.current.scrollLeft = e.target.scrollLeft;
        }
    };
    const [hashtags, setHashtags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextSkip, setNextSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [filter, setFilter] = useState('All');
    const [metricStats, setMetricStats] = useState({ activeGroups: 0, uniqueParticipants: 0, activeRecipients: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'activity', direction: 'desc' });
    const [optionsPopoverOpen, setOptionsPopoverOpen] = useState(null);
    // Modal state for View Participants / View Recipients
    const [viewModal, setViewModal] = useState({ open: false, type: null, title: '', groupId: null, hashtag: '' });
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalHasMore, setModalHasMore] = useState(false);
    const [modalNextSkip, setModalNextSkip] = useState(0);

    useEffect(() => {
        // Always fetch fresh data so metrics stay up to date
        sessionStorage.removeItem('uc_from_drilldown');
        sessionStorage.removeItem('uc_hashtag_overview_state');
        fetchHashtags(0);
        // Load active recipients async (separate API)
        fetchActiveRecipients();
    }, []);

    const fetchActiveRecipients = async () => {
        try {
            var res = await axios.get(url.textifyUniqueRecipients);
            if (res.data && res.data.uniqueRecipientCount !== undefined) {
                setMetricStats(function(prev) {
                    return Object.assign({}, prev, { activeRecipients: res.data.uniqueRecipientCount });
                });
            }
        } catch (err) {
            console.error('[Hashtag Overview] Error fetching unique recipients:', err);
        }
    };

    const fetchHashtags = async (skipValue = 0) => {
        try {
            if (skipValue === 0) setLoading(true);
            else setLoadingMore(true);

            const res = await axios.get(`${url.textifyListGroups}?skip=${skipValue}`);
            const data = (res.data && res.data.items) || [];

            const total = (res.data && res.data.total) || data.length;
            const hasMore = (res.data && res.data.hasMore !== undefined) ? res.data.hasMore : (data.length === 25);
            const nextSkip = (res.data && res.data.nextSkip !== undefined) ? res.data.nextSkip : (skipValue + data.length);

            const formattedData = data.map(item => ({
                id: item._id,
                hashtag: item.groupName.startsWith('#') ? item.groupName.slice(1) : item.groupName,
                users: item.uniqueParticipantCount || 0,
                recipients: item.recipientCount || 0,
                activity: item.messageCount || 0,
                lastActivity: item.lastActivityAt ? formatTimeAgo(item.lastActivityAt) : '—',
                lastActivityTs: item.lastActivityAt ? new Date(item.lastActivityAt).getTime() : 0,
                sentiment: (item.averageSentimentsScore && item.averageSentimentsScore.sentiment) || null,
                needsAttention: item.needsAttentionCount || 0,
                newActivity: false,
            }));

            if (skipValue === 0) {
                setHashtags(formattedData);
                // The API gives `total` for active groups and optionally `uniqueParticipants`
                const participantsSum = (res.data && res.data.uniqueParticipants) ||
                    data.reduce(function(sum, item) { return sum + (item.uniqueParticipantCount || 0); }, 0);
                setMetricStats(function(prev) {
                    return Object.assign({}, prev, { activeGroups: total, uniqueParticipants: participantsSum });
                });
            } else {
                setHashtags(prev => [...prev, ...formattedData]);
            }

            setNextSkip(nextSkip);
            setHasMore(hasMore);

        } catch (error) {
            console.error('[Hashtag Overview] Error fetching groups:', error);
        } finally {
            if (skipValue === 0) setLoading(false);
            else setLoadingMore(false);
        }
    };

    const handleBottomScroll = () => {
        if (hasMore && !loading && !loadingMore) {
            fetchHashtags(nextSkip);
        }
    };

    const handleRowClick = (hashtag, groupId) => {
        // Drop a breadcrumb that we are leaving to go to drilldown, so we know to restore scroll on return
        sessionStorage.setItem('uc_from_drilldown', 'true');
        history.push(`${match.url}/${hashtag.toLowerCase()}/${groupId}`);
    };

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const handleExportThread = async (item) => {
        try {
            var allMessages = [];
            var skip = 0;
            var pageSize = 100;
            var moreToFetch = true;

            while (moreToFetch) {
                var res = await axios.get(url.textifyGetGroupMessages(item.id) + '?limit=' + pageSize + '&skip=' + skip);
                var messages = (res.data && res.data.messages) || [];
                allMessages = allMessages.concat(messages);
                moreToFetch = !!(res.data && res.data.hasMore) && messages.length === pageSize;
                skip = skip + messages.length;
                if (skip >= 1000) break; // safety cap
            }

            if (allMessages.length === 0) {
                toastContainer('No messages to export.', 'info');
                return;
            }

            var rows = allMessages.map(function(m) {
                var senderName = (m.userId && typeof m.userId === 'object')
                    ? (m.userId.firstName + ' ' + m.userId.lastName).trim()
                    : (m.isRecipient ? 'Recipient' : 'Participant');
                var phone = (!m.isRecipient && m.userId && m.userId.mobileNumber) || '';
                var date = m.createdAt ? new Date(m.createdAt).toLocaleString() : '';
                var body = (m.body || '').replace(/"/g, '""');
                return '"' + date + '","' + senderName + '","' + phone + '","' + body + '"';
            });

            var csvContent = '"Date","Sender","Phone","Message"\n' + rows.join('\n');
            var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            var downloadUrl = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = downloadUrl;
            link.download = '#' + item.hashtag + '-thread-export.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            toastContainer('Export Complete.', 'success');
        } catch (err) {
            console.error('[HashtagOverview] Export thread error:', err);
            toastContainer('Failed to export thread.', 'error');
        }
    };

    const openViewModal = function(type, item) {
        setViewModal({ open: true, type: type, title: type === 'participants' ? 'Participants' : 'Recipients', groupId: item.id, hashtag: item.hashtag });
        setModalData([]);
        setModalNextSkip(0);
        setModalHasMore(false);
        if (type === 'participants') {
            fetchParticipants(item.id, 0);
        } else {
            fetchRecipients(item.id);
        }
    };

    var fetchParticipants = function(groupId, skipVal) {
        setModalLoading(true);
        axios.get(url.textifyGetGroupDetails(groupId) + '?participantSkip=' + skipVal)
            .then(function(res) {
                var items = (res.data && res.data.participants) || [];
                if (skipVal === 0) {
                    setModalData(items);
                } else {
                    setModalData(function(prev) { return prev.concat(items); });
                }
                setModalHasMore(!!(res.data && res.data.participantHasMore));
                setModalNextSkip((res.data && res.data.participantNextSkip) || (skipVal + items.length));
            })
            .catch(function(err) {
                console.error('[HashtagOverview] Fetch participants error:', err);
                toastContainer('Failed to load participants.', 'error');
            })
            .finally(function() {
                setModalLoading(false);
            });
    };

    var fetchRecipients = function(groupId) {
        setModalLoading(true);
        axios.get(url.textifyGetGroupDetails(groupId))
            .then(function(res) {
                var recipients = (res.data && res.data.recipients) || [];
                setModalData(recipients);
                setModalHasMore(false);
            })
            .catch(function(err) {
                console.error('[HashtagOverview] Fetch recipients error:', err);
                toastContainer('Failed to load recipients.', 'error');
            })
            .finally(function() {
                setModalLoading(false);
            });
    };

    var closeViewModal = function() {
        setViewModal({ open: false, type: null, title: '', groupId: null, hashtag: '' });
        setModalData([]);
    };

    const handleDropdownAction = (e, actionType, item) => {
        e.stopPropagation();
        setOptionsPopoverOpen(null);
        if (actionType === 'OPEN') {
            handleRowClick(item.hashtag, item.id);
        }
        if (actionType === 'EXPORT') {
            handleExportThread(item);
        }
        if (actionType === 'VIEW_PARTICIPANTS') {
            openViewModal('participants', item);
        }
        if (actionType === 'VIEW_RECIPIENTS') {
            openViewModal('recipients', item);
        }
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? <BiUpArrowAlt size={14} className="ms-1" /> : <BiDownArrowAlt size={14} className="ms-1" />;
        }
        return <BiSortAlt2 size={14} className="ms-1 text-muted" style={{ opacity: 0.5 }} />;
    };

    const filteredHashtags = hashtags.filter(h => filter === 'All' || h.sentiment === filter);
    const sortIteratee = sortConfig.key === 'hashtag'
        ? (itm) => (itm.hashtag ? itm.hashtag.toLowerCase() : '')
        : [sortConfig.key];
    const sortedHashtags = orderBy(filteredHashtags, sortIteratee, [sortConfig.direction]);

    const csvHeaders = [
        { label: "Hashtag", key: "hashtag" },
        { label: "Participants", key: "users" },
        { label: "Recipients", key: "recipients" },
        { label: "Activity", key: "activity" },
        { label: "Last Activity", key: "lastActivity" },
    ];

    const isOrgAdmin = !!(user && user.profile && user.profile.orgAdmin);
    const showEmptyState = !loading && !isOrgAdmin && hashtags.length === 0;

    return (
        <div className="uc-overview-page">
            <div className="uc-header-area">
                <h1>User Chats</h1>
                {!showEmptyState && (
                    <p>Shared SMS inbox organized by hashtag channel. Visible only to users included as hashtag recipients.</p>
                )}
            </div>

            {showEmptyState && (
                <div className="uc-empty-state">
                    <img src={loginImgTrivvy} alt="" />
                    <div className="uc-empty-state-msg">{USER_CHATS_EMPTY_STATE_MSG}</div>
                </div>
            )}

            {!showEmptyState && (
            <>
            <div className="uc-metrics-cards">
                <div className="uc-metric-card">
                    <div className="icon-wrapper"><BiHash /></div>
                    <div className="uc-metric-data">
                        <strong>{metricStats.activeGroups}</strong>
                        <span>Chat Groups</span>
                    </div>
                </div>
                <div className="uc-metric-card">
                    <div className="icon-wrapper"><BiGroup /></div>
                    <div className="uc-metric-data">
                        <strong>{metricStats.uniqueParticipants.toLocaleString()}</strong>
                        <span>Unique Participants</span>
                    </div>
                </div>
                <div className="uc-metric-card">
                    <div className="icon-wrapper"><BiUser /></div>
                    <div className="uc-metric-data">
                        <strong>{metricStats.activeRecipients.toLocaleString()}</strong>
                        <span>Unique Recipients</span>
                    </div>
                </div>
            </div>

            <div className="uc-table-actions">
                <div className="uc-sentiment-filter">
                    {/* Sentiment filtering UI is not included in this phase */}
                </div>
                <CSVLink data={sortedHashtags} headers={csvHeaders} filename={`User-Chats-Export.csv`} style={{ textDecoration: 'none' }}>
                    <button className="uc-export-btn"><BsDownload /> Export CSV</button>
                </CSVLink>
            </div>

            {/* Two-table sticky-header pattern: the header table sits above
                a separate scrolling body table. The vertical scrollbar lives
                on the body container only, so it visually starts at the
                first body row instead of alongside the column headers.
                Both tables share matching <colgroup> widths to keep columns
                aligned. */}
            <div className="uc-table-frame">
                <div className="uc-table-header-wrap" ref={headerWrapRef}>
                    <table className="uc-table uc-table-cards uc-table-fixed">
                        <colgroup>
                            <col style={{ width: '23%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '21%' }} />
                            <col style={{ width: '9%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className="cursor-pointer select-none" onClick={() => handleSort('hashtag')}>
                                    HASHTAG {renderSortIcon('hashtag')}
                                </th>
                                <th className="select-none text-center">
                                    <div className="d-flex align-items-center justify-content-center cursor-pointer"><div className="uc-th-spacer"></div>
                                        <div className="d-flex align-items-center" onClick={() => handleSort('users')}>
                                            <BiGroup className="me-1" size={14} />PARTICIPANTS {renderSortIcon('users')}
                                        </div>
                                        <div className="uc-th-popover" onClick={e => e.stopPropagation()}>
                                            <OoPopover
                                                target="participants-tooltip"
                                                placement="top"
                                                headerText="Participants"
                                                bodyText="Number of unique users who have sent at least one chat with this hashtag."
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th className="select-none text-center">
                                    <div className="d-flex align-items-center justify-content-center cursor-pointer"><div className="uc-th-spacer"></div>
                                        <div className="d-flex align-items-center" onClick={() => handleSort('recipients')}>
                                            <BiUser className="me-1" size={14} />RECIPIENTS {renderSortIcon('recipients')}
                                        </div>
                                        <div className="uc-th-popover" onClick={e => e.stopPropagation()}>
                                            <OoPopover
                                                target="recipients-tooltip"
                                                placement="top"
                                                headerText="Recipients"
                                                bodyText="Number of unique recipients in this hashtag."
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th className="select-none text-center">
                                    <div className="d-flex align-items-center justify-content-center cursor-pointer"><div className="uc-th-spacer"></div>
                                        <div className="d-flex align-items-center" onClick={() => handleSort('activity')}>
                                            ACTIVITY {renderSortIcon('activity')}
                                        </div>
                                        <div className="uc-th-popover" onClick={e => e.stopPropagation()}>
                                            <OoPopover
                                                target="activity-tooltip"
                                                placement="top"
                                                headerText="Activity"
                                                bodyText="Total number of chats by participants and recipients."
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th className="select-none text-center">
                                    <div className="d-flex align-items-center justify-content-center cursor-pointer"><div className="uc-th-spacer"></div>
                                        <div className="d-flex align-items-center" onClick={() => handleSort('lastActivityTs')}>
                                            <BiTimeFive className="me-1" size={14} />LAST ACTIVITY {renderSortIcon('lastActivityTs')}
                                        </div>
                                        <div className="uc-th-popover" onClick={e => e.stopPropagation()}>
                                            <OoPopover
                                                target="last-activity-tooltip"
                                                placement="top"
                                                headerText="Last Activity"
                                                bodyText="Last chat sent by a participant or a recipient."
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                    </table>
                </div>

                <BottomScrollListener offset={50} onBottom={handleBottomScroll}>
                    {(scrollRef) => (
                        <div
                            className="uc-table-container"
                            onScroll={syncHeaderHorizontalScroll}
                            ref={(node) => {
                                if (typeof scrollRef === 'function') {
                                    scrollRef(node);
                                } else if (scrollRef && typeof scrollRef === 'object') {
                                    scrollRef.current = node;
                                }
                                containerRef.current = node;
                            }}
                        >
                            <table className="uc-table uc-table-cards uc-table-fixed">
                                <colgroup>
                                    <col style={{ width: '23%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '16%' }} />
                                    <col style={{ width: '13%' }} />
                                    <col style={{ width: '21%' }} />
                                    <col style={{ width: '9%' }} />
                                </colgroup>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6" className="text-center py-4">Loading...</td></tr>
                                    ) : (
                                        <>
                                            {sortedHashtags.map(item => (
                                                <tr key={item.id} onClick={() => handleRowClick(item.hashtag, item.id)} className="cursor-pointer uc-card-row">
                                                    <td>
                                                        <div className="uc-hashtag-col text-bold">
                                                            <span className="hash-icon">#</span>
                                                            <span className="uc-hashtag-text" title={orgPrefix ? orgPrefix + '-' + item.hashtag.toLowerCase() : item.hashtag}>
                                                                {orgPrefix ? orgPrefix + '-' + item.hashtag.toLowerCase() : item.hashtag}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="uc-fw-500 text-center">{item.users}</td>
                                                    <td className="uc-fw-500 text-center">{item.recipients}</td>
                                                    <td className="uc-fw-500 text-center">{item.activity}</td>
                                                    <td className="uc-fw-400-muted text-center">{item.lastActivity}</td>
                                                    <td onClick={e => e.stopPropagation()}>
                                                        <div className="cursor-pointer" style={{ position: 'relative' }}>
                                                            <span
                                                                id={`hashtagListRow_${item.id}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOptionsPopoverOpen(optionsPopoverOpen === item.id ? null : item.id);
                                                                }}
                                                                className="cursor-pointer">
                                                                <BsThreeDots className="text-muted" size={18} />
                                                            </span>

                                                            <Popover
                                                                isOpen={optionsPopoverOpen === item.id}
                                                                target={`hashtagListRow_${item.id}`}
                                                                toggle={() => setOptionsPopoverOpen(null)}
                                                                placement="bottom"
                                                                hideArrow
                                                                className="surveyListRow_Popover">
                                                                <PopoverBody className="p-0 py-2">
                                                                    <div
                                                                        className="dropdown-item py-1 px-3 cursor-pointer"
                                                                        onClick={(e) => handleDropdownAction(e, 'OPEN', item)}>
                                                                        View Conversations
                                                                    </div>
                                                                    <div
                                                                        className="dropdown-item py-1 px-3 cursor-pointer"
                                                                        onClick={(e) => handleDropdownAction(e, 'VIEW_PARTICIPANTS', item)}>
                                                                        View Participants
                                                                    </div>
                                                                    <div
                                                                        className="dropdown-item py-1 px-3 cursor-pointer"
                                                                        onClick={(e) => handleDropdownAction(e, 'VIEW_RECIPIENTS', item)}>
                                                                        View Recipients
                                                                    </div>
                                                                    <div
                                                                        className="dropdown-item py-1 px-3 cursor-pointer"
                                                                        onClick={(e) => handleDropdownAction(e, 'EXPORT', item)}>
                                                                        Export
                                                                    </div>
                                                                </PopoverBody>
                                                            </Popover>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {loadingMore && (
                                                <tr><td colSpan="6" className="text-center py-3 text-muted">Loading more groups...</td></tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BottomScrollListener>
            </div>
            </>
            )}

            {/* View Participants / View Recipients Modal */}
            <Modal isOpen={viewModal.open} toggle={closeViewModal} size="md">
                <ModalHeader toggle={closeViewModal}>
                    {viewModal.title} — #{orgPrefix ? orgPrefix + '-' + viewModal.hashtag.toLowerCase() : viewModal.hashtag}
                </ModalHeader>
                <ModalBody>
                    {modalLoading && modalData.length === 0 ? (
                        <p className="text-center text-muted">Loading...</p>
                    ) : modalData.length === 0 ? (
                        <p className="text-center text-muted">No {viewModal.type === 'participants' ? 'participants' : 'recipients'} found.</p>
                    ) : (
                        <div>
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Phone Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalData.map(function(person, idx) {
                                        var personName = person.name || ((person.firstName || '') + ' ' + (person.lastName || '')).trim() || 'Not Registered';
                                        var phone = person.mobileNumber || '—';
                                        return (
                                            <tr key={person.userId || idx}>
                                                <td>{personName}</td>
                                                <td>{phone}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {viewModal.type === 'participants' && modalHasMore && (
                                <div className="text-center mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        disabled={modalLoading}
                                        onClick={function() { fetchParticipants(viewModal.groupId, modalNextSkip); }}>
                                        {modalLoading ? 'Loading...' : 'Load more'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
};
export default HashtagOverview;
